import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  OPPORTUNITY_ACTIVITY_FILTERS,
  type OpportunityActivityFilter,
} from "./opportunityActivityEntries";

const FILTER_LABELS: Record<OpportunityActivityFilter, string> = {
  all: "All",
  changes: "Changes",
  quotes: "Quotes",
  messages: "Messages",
  views: "Views",
};
const TEAM_VIEWS_SWITCH_ID = "opportunity-activity-team-views";

interface OpportunityActivityFiltersProps {
  filter: OpportunityActivityFilter;
  onFilterChange: (filter: OpportunityActivityFilter) => void;
  showTeamViews: boolean;
  onShowTeamViewsChange: (show: boolean) => void;
}

/**
 * The activity log's filter row: a segmented filter and the team views switch.
 * The segments scroll sideways and the switch wraps below in a narrow sheet.
 * @param filter - The selected filter
 * @param onFilterChange - Selects a filter
 * @param showTeamViews - Whether quote views by team members are shown
 * @param onShowTeamViewsChange - Shows or hides team quote views
 */
export const OpportunityActivityFilters = ({
  filter,
  onFilterChange,
  showTeamViews,
  onShowTeamViewsChange,
}: OpportunityActivityFiltersProps) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-0.5">
    <ToggleGroup
      type="single"
      value={filter}
      onValueChange={(value) => {
        const selected = OPPORTUNITY_ACTIVITY_FILTERS.find(
          (option) => option === value,
        );
        if (selected) {
          onFilterChange(selected);
        }
      }}
      aria-label="Filter activity"
      className="max-w-full justify-start gap-0.5 overflow-x-auto rounded-md bg-muted p-0.5"
    >
      {OPPORTUNITY_ACTIVITY_FILTERS.map((option) => (
        <ToggleGroupItem
          key={option}
          value={option}
          size="sm"
          className="h-7 min-w-0 shrink-0 rounded-[5px] px-2.5 text-xs text-muted-foreground hover:bg-transparent hover:text-foreground data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
        >
          {FILTER_LABELS[option]}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
    <div className="ml-auto flex items-center gap-2">
      <Switch
        id={TEAM_VIEWS_SWITCH_ID}
        checked={showTeamViews}
        onCheckedChange={onShowTeamViewsChange}
      />
      <Label
        htmlFor={TEAM_VIEWS_SWITCH_ID}
        className="whitespace-nowrap text-xs font-normal text-muted-foreground"
      >
        Team views
      </Label>
    </div>
  </div>
);
